import { ReactNode, Suspense, createContext, useContext, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { Separator } from '@givebutter/primitives/separator';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal, unmaskNumber } from '@givebutter/util';
import {
    faCreditCard,
    faEnvelope,
    faMoneyBill,
    faTimes,
    faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchAuctionBidder } from 'api/campaigns';
import { PaymentMethodsPresentational } from 'assets/js/pages/transactions/TransactionMethod';
import { AddressForm } from 'components/address-form/AddressForm';
import { AddressFormValues } from 'components/address-form/types';
import * as EditableInput from 'components/editable-input';
import { useGivebutterFormContext } from 'components/form';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import { STEPS } from 'components/pages/campaign-settings/campaign-auction/item-details/declare-winner/constants';
import { DeclareWinnerDialogForm } from 'components/pages/campaign-settings/campaign-auction/item-details/declare-winner/types';
import {
    getItemSubtotal,
    getRequiresShippingAddress,
} from 'components/pages/campaign-settings/campaign-auction/item-details/declare-winner/utils';
import { SelectItemWithLabel, SelectListItem, UseFormSearchableSelect } from 'components/select';
import * as Tooltip from 'components/tooltip';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAuction } from 'customHooks/useCurrentAuction';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteBidders } from 'customHooks/useInfiniteBidders';
import { capitalize } from 'lodash';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { twMerge } from 'tailwind-merge';
import { AuctionItem, Bidder, BidderPaymentPreview } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ItemWinnerFormContext {
    item: AuctionItem;
    bidder: Bidder;
    currentStep: string;
    paymentPreview: BidderPaymentPreview | null;
}

interface ItemWinnerFormProps extends ItemWinnerFormContext {
    children: ReactNode;
}

const defaultContext: ItemWinnerFormContext = {
    item: {} as AuctionItem,
    bidder: {} as Bidder,
    currentStep: '',
    paymentPreview: null,
};

const ItemWinnerContext = createContext<ItemWinnerFormContext>(defaultContext);

const useItemWinnerForm = () => {
    const context = useContext(ItemWinnerContext);
    if (!context) {
        return defaultContext;
    }
    return context;
};

const ItemWinnerForm = ({
    children,
    item,
    bidder,
    currentStep,
    paymentPreview,
}: ItemWinnerFormProps) => {
    const value = useMemo(
        () => ({
            item,
            bidder,
            currentStep,
            paymentPreview,
        }),
        [item, bidder, currentStep, paymentPreview],
    );

    return <ItemWinnerContext.Provider value={value}>{children}</ItemWinnerContext.Provider>;
};

const ItemWinnerFormStepDescription = () => {
    const { currentStep } = useItemWinnerForm();
    const methods = Form.useGivebutterFormContext<DeclareWinnerDialogForm>();
    const { watch } = methods;
    const paymentOption = watch('paymentOption');

    switch (currentStep) {
        case STEPS.DECLARE_WINNER:
            return (
                <p className="text-sm">
                    The current highest bidder will be declared the winner. Proceed to the next step
                    if this information is correct:
                </p>
            );

        case STEPS.ENTER_WINNER_INFO:
            return <p className="text-sm">Select a winner and confirm the purchase price:</p>;

        case STEPS.CHOOSE_PAYMENT:
            return <p className="text-sm">Choose a payment option below:</p>;

        case STEPS.FINALIZE_WINNER:
            return (
                <p className="text-sm">
                    {paymentOption === 'charge' &&
                        `Confirm the bidder's info before charging the payment method:`}
                    {paymentOption === 'invoice' &&
                        `Confirm the bidder's info before sending a payment request:`}
                    {paymentOption === 'offline' &&
                        `Confirm the bidder's info and select a payment method:`}
                </p>
            );
    }
};

const ItemWinnerFormDeclareWinnerStep = () => {
    const { currentStep, bidder, item } = useItemWinnerForm();
    const methods = Form.useGivebutterFormContext<DeclareWinnerDialogForm>();
    const { control, path } = methods;
    if (currentStep !== STEPS.DECLARE_WINNER) return null;
    const requireAddress = getRequiresShippingAddress(bidder, item);

    return (
        <div className="flex flex-col gap-6">
            <ItemWinnerFormBidderInfo />
            {requireAddress && (
                <div className="flex flex-col gap-2">
                    <p className="text-sm">Please update the bidder's address.</p>
                    <AddressForm isRequired={requireAddress} />
                </div>
            )}
            <div className="flex flex-col gap-2">
                <Form.Label className="text-gray-600" name={path('highestBid')}>
                    Highest bid
                </Form.Label>
                <Form.TextInputRoot
                    control={control}
                    name={path('highestBid')}
                    inputRootProps={{
                        mask: 'decimal',
                    }}
                >
                    <InputCurrencyIcon />
                    <Form.TextInputForm />
                </Form.TextInputRoot>
                <p className="text-sm text-gray-500">Adjust if the bid amount is different.</p>
            </div>
        </div>
    );
};

const ItemWinnerFormBidderInfo = () => {
    const { bidder } = useItemWinnerForm();

    return (
        <div className="flex gap-8">
            <div>
                <strong className="text-sm text-gray-400">Name</strong>
                <p className="text-sm">
                    {bidder?.first_name} {bidder?.last_name}
                </p>
            </div>
            <div>
                <strong className="text-sm text-gray-400">Email</strong>
                <p className="text-sm">{bidder?.email}</p>
            </div>
            <div>
                <strong className="text-sm text-gray-400">Bidder #</strong>
                <p className="text-sm">{bidder?.number}</p>
            </div>
        </div>
    );
};

const ItemWinnerFormEnterInfoStep = () => {
    const { item, bidder, currentStep } = useItemWinnerForm();
    const currencySymbol = useCurrencySymbol();
    const methods = Form.useGivebutterFormContext<DeclareWinnerDialogForm>();
    const { control, path, clearErrors, watch, setValue } = methods;
    if (currentStep !== STEPS.ENTER_WINNER_INFO) return null;

    const buyNowPrice = watch('buyNowPrice');
    const numericValue = unmaskNumber(buyNowPrice || '0');
    const minimumPrice = item.price ?? 0;
    const showPriceWarning = buyNowPrice && numericValue < minimumPrice;
    const requireAddress = getRequiresShippingAddress(bidder, item);

    const resetSelectedBidder = () => {
        setValue('paymentOption', 'charge');
        setValue('selectedBidder', null);
        setValue('paymentMethodId', null);
    };

    return (
        <div className="flex flex-col gap-4">
            <EditableInput.Root>
                <Form.Container>
                    <Form.Label name={path('selectedBidder')}>Bidder name or #</Form.Label>
                    <div className="flex flex-col gap-4">
                        <div className="flex items-start">
                            <div className={twMerge('', !bidder && 'flex-1')}>
                                {!bidder && (
                                    <Suspense fallback={null}>
                                        <ItemWinnerFormBidderSelect />
                                    </Suspense>
                                )}
                                {!!bidder && (
                                    <div className="flex flex-col gap-1">
                                        <p className="font-medium text-gray-600">
                                            {bidder?.first_name} {bidder?.last_name}
                                        </p>
                                        <p className="text-gray-600">{bidder?.email}</p>
                                        <p className="text-gray-600">{bidder?.phone}</p>
                                    </div>
                                )}
                                <Form.Error name={path('selectedBidder')} />
                            </div>
                            {!!bidder && (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="transparent"
                                    shape={'square'}
                                    onClick={() => {
                                        clearErrors();
                                        resetSelectedBidder();
                                    }}
                                    className="relative"
                                    aria-label="Close"
                                >
                                    <FontAwesomeIcon
                                        className="absolute top-0"
                                        icon={faTimes}
                                        size="lg"
                                    />
                                </Button>
                            )}
                        </div>
                        {!!bidder && requireAddress && (
                            <div className="flex flex-col gap-2">
                                <p className="text-sm">Please update the bidder's address.</p>
                                <AddressForm isRequired={requireAddress} />
                            </div>
                        )}
                    </div>
                </Form.Container>
            </EditableInput.Root>
            <Form.Container className="flex flex-col gap-2">
                <div>
                    <Form.Label name={path('buyNowPrice')}>Buy now price</Form.Label>
                    <Form.TextInputRoot control={control} name={path('buyNowPrice')}>
                        <InputCurrencyIcon />
                        <Form.TextInputForm />
                    </Form.TextInputRoot>
                </div>
                <div className="flex min-h-11 flex-col gap-1">
                    {showPriceWarning && (
                        <p className="text-sm text-amber-600">
                            Purchase amount is less than the {currencySymbol}
                            {maskDecimal(minimumPrice)} buy now price.
                        </p>
                    )}
                    <Form.Error name={path('buyNowPrice')} />
                    <p className="text-sm text-gray-500">
                        Adjust if the purchase amount is different.
                    </p>
                </div>
            </Form.Container>
        </div>
    );
};

const ItemWinnerFormChoosePaymentStep = () => {
    const { currentStep, bidder } = useItemWinnerForm();
    const methods = Form.useGivebutterFormContext<DeclareWinnerDialogForm>();
    const { control, path } = methods;

    if (currentStep !== STEPS.CHOOSE_PAYMENT) return null;

    const noPaymentMethods = !bidder.payment_methods || bidder.payment_methods.length === 0;

    return (
        <div className="flex flex-col gap-2">
            <Form.Label className="sr-only" name="subtype">
                Payment Options
            </Form.Label>
            <Form.UseFormRadioGroup
                orientation={'horizontal'}
                name={'paymentOption'}
                control={control}
                className="flex-col gap-y-2 sm:flex-col"
                variant={'card'}
            >
                <RadioGroup.Item
                    value="charge"
                    className={'flex gap-x-3'}
                    disabled={noPaymentMethods}
                >
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                        <FontAwesomeIcon icon={faCreditCard} size="lg" className="text-gray-500" />
                    </div>
                    <div className={'flex flex-1 flex-col'}>
                        <Form.Label
                            name={path('paymentOption')}
                            htmlFor={'charge'}
                            className={'font-medium'}
                        >
                            Charge saved payment method
                        </Form.Label>
                        {noPaymentMethods ? (
                            <div className="flex items-center gap-1 text-sm text-orange-700">
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                                No payment method on file.
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500">
                                {`${bidder?.first_name} will be charged immediately.`}
                            </div>
                        )}
                    </div>
                    <RadioGroup.IndicatorContainer>
                        <RadioGroup.Indicator />
                    </RadioGroup.IndicatorContainer>
                </RadioGroup.Item>

                <RadioGroup.Item value="invoice" className={'flex gap-x-3'}>
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                        <FontAwesomeIcon icon={faEnvelope} size="lg" className="text-gray-500" />
                    </div>
                    <div className={'flex flex-1 flex-col'}>
                        <Form.Label
                            name={path('paymentOption')}
                            htmlFor={'invoice'}
                            className={'font-medium'}
                        >
                            Send payment request
                        </Form.Label>
                        <div className="text-sm text-gray-500">
                            {`${bidder?.first_name} will be sent a payment request via email.`}
                        </div>
                    </div>
                    <RadioGroup.IndicatorContainer>
                        <RadioGroup.Indicator />
                    </RadioGroup.IndicatorContainer>
                </RadioGroup.Item>

                <RadioGroup.Item value="offline" className={'flex gap-x-3'}>
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                        <FontAwesomeIcon icon={faMoneyBill} size="lg" className="text-gray-500" />
                    </div>
                    <div className={'flex flex-1 flex-col'}>
                        <Form.Label
                            name={path('paymentOption')}
                            htmlFor={'offline'}
                            className={'font-medium'}
                        >
                            Record offline payment
                        </Form.Label>
                        <div className="text-sm text-gray-500">
                            Mark this payment as received outside of Givebutter.
                        </div>
                    </div>
                    <RadioGroup.IndicatorContainer>
                        <RadioGroup.Indicator />
                    </RadioGroup.IndicatorContainer>
                </RadioGroup.Item>
            </Form.UseFormRadioGroup>
        </div>
    );
};

const ItemWinnerFormFinalizeWinnerStep = () => {
    const { currentStep, bidder } = useItemWinnerForm();
    const formMethods = Form.useGivebutterFormContext<DeclareWinnerDialogForm>();
    const { watch, control, path } = formMethods;
    const paymentOption = watch('paymentOption');

    if (currentStep !== STEPS.FINALIZE_WINNER) return null;

    const [offlinePaymentMethod, paymentMethodId] = watch([
        'offlinePaymentMethod',
        'paymentMethodId',
    ]);

    const selectedPayment =
        paymentMethodId != null
            ? bidder.payment_methods.find(p => p.id === Number(paymentMethodId))
            : null;

    return (
        <>
            <ItemWinnerFormBidderInfo />
            <div className="flex flex-col gap-2">
                <Label className="text-gray-400">Payment method</Label>
                {paymentOption === 'charge' && bidder?.payment_methods?.length > 0 && (
                    <Form.SingleSelect control={control} name="paymentMethodId">
                        <SingleSelect.Trigger aria-label="Payment method">
                            <SingleSelect.Value>
                                {selectedPayment ? (
                                    <PaymentMethodsPresentational payment={selectedPayment} />
                                ) : (
                                    'Select payment method'
                                )}
                            </SingleSelect.Value>
                        </SingleSelect.Trigger>
                        <SingleSelect.Content>
                            {bidder.payment_methods.map(payment => (
                                <SingleSelect.Item value={payment.id.toString()} key={payment.id}>
                                    <SingleSelect.ItemText>
                                        <PaymentMethodsPresentational payment={payment} />
                                    </SingleSelect.ItemText>
                                </SingleSelect.Item>
                            ))}
                        </SingleSelect.Content>
                    </Form.SingleSelect>
                )}
                {paymentOption === 'invoice' && (
                    <div className="flex gap-4 rounded-xl border p-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                            <FontAwesomeIcon
                                icon={faEnvelope}
                                size="1x"
                                className="text-gray-500"
                            />
                        </div>
                        <div className="flex flex-col">
                            <Label className="text-sm text-gray-500">Send payment request</Label>
                            <span className="text-sm text-gray-500">
                                {bidder?.first_name} will be sent a payment request via email.
                            </span>
                        </div>
                    </div>
                )}
                {paymentOption === 'offline' && (
                    <div className="flex flex-col gap-2">
                        <Form.SingleSelect control={control} name="offlinePaymentMethod">
                            <SingleSelect.Trigger aria-label="Offline payment method">
                                <SingleSelect.Value>
                                    {capitalize(offlinePaymentMethod)}
                                </SingleSelect.Value>
                            </SingleSelect.Trigger>
                            <SingleSelect.Content>
                                <SingleSelect.Item value="cash">
                                    <SingleSelect.ItemText>Cash</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value="check">
                                    <SingleSelect.ItemText>Check</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value="in-kind">
                                    <SingleSelect.ItemText>In-kind</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value="stock">
                                    <SingleSelect.ItemText>Stock</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value="property">
                                    <SingleSelect.ItemText>Property</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value="other">
                                    <SingleSelect.ItemText>Other</SingleSelect.ItemText>
                                </SingleSelect.Item>
                            </SingleSelect.Content>
                        </Form.SingleSelect>
                        {offlinePaymentMethod === 'check' && (
                            <div className="flex gap-2">
                                <Form.Container>
                                    <Form.Label name={'checkNumber'} className={'text-gray-400'}>
                                        Check number
                                    </Form.Label>
                                    <Form.TextInputRoot
                                        control={control}
                                        name={path('checkNumber')}
                                    >
                                        <Form.TextInputForm />
                                    </Form.TextInputRoot>
                                    <Form.Error name={path('checkNumber')} />
                                </Form.Container>
                                <Form.Container>
                                    <Form.Label
                                        name={'checkDepositDate'}
                                        className={'text-gray-400'}
                                    >
                                        Deposit date
                                    </Form.Label>
                                    <Form.TextInputRoot
                                        control={control}
                                        name={path('checkDepositDate')}
                                        inputRootProps={{
                                            mask: 'date',
                                        }}
                                    >
                                        <Form.TextInputForm placeholder={'mm/dd/yyyy'} />
                                    </Form.TextInputRoot>
                                    <Form.Error name={path('checkDepositDate')} />
                                </Form.Container>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <ItemWinnerFormBidSummary />
        </>
    );
};

const ItemWinnerFormBidSummary = () => {
    const { item, paymentPreview } = useItemWinnerForm();
    const currencySymbol = useCurrencySymbol();
    const methods = Form.useGivebutterFormContext<DeclareWinnerDialogForm>();
    const { watch } = methods;
    const paymentOption = watch('paymentOption');
    const [highestBid, buyNowPrice] = watch(['highestBid', 'buyNowPrice']);
    const subtotal = getItemSubtotal(item, highestBid, buyNowPrice);
    const paymentText =
        paymentOption === 'charge'
            ? `A 5% platform fee is applied to the transaction in addition to the 2.9% + $0.30 processing fee. Fees may vary depending on the donor's payment method.`
            : `Payment processing fees may vary based on the payment method used by the winner.`;

    if (!paymentPreview) return null;

    return (
        <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">Bid summary</p>
            <div className="flex flex-col gap-4 rounded-xl bg-gray-50 p-4">
                <div className="flex justify-between">
                    <span className="text-sm text-blue-500">{item.name}</span>
                    <span className="text-sm">
                        {currencySymbol}
                        {maskDecimal(subtotal ?? 0)}
                    </span>
                </div>
                <div className="flex justify-between">
                    <span className="text-sm">Fees</span>
                    <span className="text-sm">
                        {currencySymbol}
                        {maskDecimal(paymentPreview.fee)}
                    </span>
                </div>
                <div className="flex justify-between">
                    <span className="text-sm">Shipping</span>
                    <span className="text-sm">
                        {currencySymbol}
                        {maskDecimal(paymentPreview.shipping)}
                    </span>
                </div>
                <Separator className="bg-gray-200" />
                <div className="flex justify-between">
                    <span className="text-sm font-medium">Total</span>
                    <span className="text-sm">
                        {currencySymbol}
                        {maskDecimal(paymentPreview.total)}
                    </span>
                </div>
            </div>
            <span className="text-xs text-gray-600">{paymentText}</span>
        </div>
    );
};

interface ItemWinnerFormNextButtonProps {
    canProgress: (formData: DeclareWinnerDialogForm) => boolean;
    goToNextStep: () => void;
    isSubmitting: boolean;
}

const ItemWinnerFormNextButton = (props: ItemWinnerFormNextButtonProps) => {
    const { currentStep, item, paymentPreview, bidder } = useItemWinnerForm();
    const { canProgress, goToNextStep, isSubmitting } = props;
    const currencySymbol = useCurrencySymbol();
    const methods = Form.useGivebutterFormContext<DeclareWinnerDialogForm>();
    const { watch, setValue } = methods;
    const formData = watch();
    const { paymentOption, highestBid } = formData;
    const requireAddress = getRequiresShippingAddress(bidder, item);
    const hasNoPaymentMethods = !bidder?.payment_methods || bidder?.payment_methods.length === 0;

    if (currentStep === STEPS.DECLARE_WINNER) {
        const currentBid = Number(highestBid);
        const reservePrice = item.reserve_price;

        if (reservePrice && currentBid < reservePrice) {
            return (
                <AlertDialog.Root type="warning">
                    <AlertDialog.Trigger asChild>
                        <Button variant="solid" disabled={!canProgress(formData)}>
                            Next
                        </Button>
                    </AlertDialog.Trigger>
                    <AlertDialog.Portal>
                        <AlertDialog.Overlay />
                        <AlertDialog.Content>
                            <AlertDialog.Header>
                                <AlertDialog.Icon customIcon={faTriangleExclamation} />
                                <AlertDialog.Close />
                            </AlertDialog.Header>
                            <AlertDialog.Body>
                                <AlertDialog.Title>Reserve price not met</AlertDialog.Title>
                                <AlertDialog.Description>
                                    The reserve price of {currencySymbol}
                                    {maskDecimal(reservePrice)} is not met on this item. Are you
                                    sure you want to declare a winner without meeting the reserve
                                    price?
                                </AlertDialog.Description>
                            </AlertDialog.Body>
                            <AlertDialog.Footer>
                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                <AlertDialog.Action onClick={goToNextStep}>
                                    Proceed
                                </AlertDialog.Action>
                            </AlertDialog.Footer>
                        </AlertDialog.Content>
                    </AlertDialog.Portal>
                </AlertDialog.Root>
            );
        }
    }

    const handleGoToNextStep = () => {
        if (
            (currentStep === STEPS.ENTER_WINNER_INFO || currentStep === STEPS.DECLARE_WINNER) &&
            hasNoPaymentMethods
        ) {
            setValue('paymentOption', 'invoice');
        }

        goToNextStep();
    };

    if (
        (currentStep === STEPS.DECLARE_WINNER || currentStep === STEPS.ENTER_WINNER_INFO) &&
        requireAddress
    ) {
        return (
            <Form.SubmitButton
                disabled={!canProgress(formData)}
                isSubmitting={isSubmitting}
                onClick={() => hasNoPaymentMethods && setValue('paymentOption', 'invoice')}
            >
                Next
            </Form.SubmitButton>
        );
    }

    if (currentStep === STEPS.CHOOSE_PAYMENT) {
        return (
            <Form.SubmitButton disabled={!canProgress(formData)} isSubmitting={isSubmitting}>
                Next
            </Form.SubmitButton>
        );
    }

    if (currentStep === STEPS.FINALIZE_WINNER) {
        const buttonText = paymentOption === 'invoice' ? 'Request' : 'Charge';
        return (
            <Form.SubmitButton disabled={!canProgress(formData)} isSubmitting={isSubmitting}>
                <div className="flex gap-1">
                    <span>{buttonText}</span>
                    <span>
                        {currencySymbol}
                        {maskDecimal(paymentPreview?.total ?? 0)}
                    </span>
                </div>
            </Form.SubmitButton>
        );
    }

    return (
        <Button variant="solid" disabled={!canProgress(formData)} onClick={handleGoToNextStep}>
            Next
        </Button>
    );
};

interface ItemWinnerFormBidderSelectType extends Partial<Bidder>, AddressFormValues {
    selectedBidder: SelectItemWithLabel<Bidder>[];
    paymentMethodId?: number;
}

const ItemWinnerFormBidderSelect = () => {
    const { item } = useItemWinnerForm();
    const { control, setValue, getValues, clearErrors } =
        useGivebutterFormContext<ItemWinnerFormBidderSelectType>();
    const [searchBidders, setSearchBidders] = useState('');
    const debouncedSearchBidders = useDebounce(searchBidders);
    const { data: biddersData, ref: biddersRef } = useInfiniteBidders({
        query: debouncedSearchBidders,
    });

    const setDefaultAddress = () => {
        setValue('line1', '');
        setValue('line2', '');
        setValue('city', '');
        setValue('stateUs', '');
        setValue('stateIntl', '');
        setValue('zip', '');
        setValue('country', 'USA');
    };

    const { selectBidder } = useBidderSelect({
        onSuccess: bidder => {
            const requireAddress = getRequiresShippingAddress(bidder, item);

            setValue('selectedBidder', [
                {
                    ...getValues('selectedBidder')[0],
                    value: bidder,
                },
            ]);

            if (bidder.payment_methods?.length > 0) {
                setValue(
                    'paymentMethodId',
                    bidder.default_payment_method?.id ?? bidder.payment_methods[0]?.id,
                );
            }

            if (requireAddress) {
                setDefaultAddress();
            }
        },
    });

    const bidders = biddersData?.pages ?? [];

    return (
        <UseFormSearchableSelect
            control={control}
            name={'selectedBidder'}
            localSearch={false}
            clearable={true}
            rules={{
                required: 'Select a bidder',
            }}
            inputProps={{
                placeholder: 'Select a bidder',
                onChange: e => {
                    setSearchBidders(e.currentTarget.value);
                },
            }}
            onSelectItem={value => {
                clearErrors();

                if (!value) {
                    setSearchBidders('');
                    return;
                }

                if (value.id) {
                    selectBidder(parseInt(value.id));
                }
            }}
        >
            {bidders.length === 0 ? (
                <div className="cursor-not-allowed p-2">
                    <p className="text-sm text-gray-500">No other bidders available</p>
                </div>
            ) : (
                bidders.map((bidderItem: Bidder) => (
                    <SelectListItem
                        key={bidderItem.id}
                        value={{
                            id: bidderItem.id.toString(),
                            label: getSelectedBidderLabel(bidderItem),
                            value: bidderItem,
                        }}
                    >
                        <div className={'flex flex-col gap-2'}>
                            <div className={'flex gap-2'}>
                                <p className="font-medium">{`${bidderItem.first_name} ${bidderItem.last_name}`}</p>
                                {(!bidderItem.payment_methods ||
                                    bidderItem.payment_methods.length === 0) && (
                                    <Tooltip.Root>
                                        <Tooltip.Trigger>
                                            <span className="sr-only">Alert</span>
                                            <FontAwesomeIcon
                                                icon={faTriangleExclamation}
                                                className="text-yellow-500"
                                            />
                                        </Tooltip.Trigger>
                                        <Tooltip.Portal>
                                            <Tooltip.Content side="right" align="center">
                                                <Tooltip.Arrow />
                                                <p>Needs payment information</p>
                                            </Tooltip.Content>
                                        </Tooltip.Portal>
                                    </Tooltip.Root>
                                )}
                            </div>
                            {bidderItem.email && (
                                <p className="font-thin text-gray-600">{`${bidderItem.email}`}</p>
                            )}
                        </div>
                    </SelectListItem>
                ))
            )}
            <div ref={biddersRef} />
        </UseFormSearchableSelect>
    );
};

interface UseBidderSelectProps {
    onSuccess?: (bidder: Bidder) => void;
}

const useBidderSelect = ({ onSuccess }: UseBidderSelectProps = {}) => {
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();

    const { data: auction } = useCurrentAuction();
    const auctionId = auction.id;

    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign.id;

    const { error } = useToast();

    const { mutate: selectBidder, isPending: isLoadingBidder } = useMutation({
        mutationFn: (bidderId: number) => {
            const data = queryClient.getQueryData<Bidder>(
                campaignQueryKeys.auctionBidderById(accountId, auctionId, bidderId),
            );

            if (data) {
                return Promise.resolve(data);
            }

            return fetchAuctionBidder(campaignId, auctionId, bidderId);
        },
        onSuccess: bidder => {
            queryClient.setQueryData(
                campaignQueryKeys.auctionBidderById(accountId, auctionId, bidder?.id),
                bidder,
            );
            onSuccess?.(bidder);
        },
        onError: (err: GivebutterAxiosError) => {
            error(err.response?.data?.message ?? 'An unknown error occurred');
        },
    });

    return { selectBidder, isLoadingBidder };
};

const getSelectedBidderLabel = (bidder: Bidder) => {
    const fullName = `${bidder?.first_name} ${bidder?.last_name}`;

    if (!bidder?.email) {
        return fullName;
    }

    return `${fullName} - ${bidder?.email}`;
};

const Root = ItemWinnerForm;
const BidderSelect = ItemWinnerFormBidderSelect;
const StepDescription = ItemWinnerFormStepDescription;
const DeclareWinnerStep = ItemWinnerFormDeclareWinnerStep;
const EnterInfoStep = ItemWinnerFormEnterInfoStep;
const ChoosePaymentStep = ItemWinnerFormChoosePaymentStep;
const FinalizeWinnerStep = ItemWinnerFormFinalizeWinnerStep;
const NextButton = ItemWinnerFormNextButton;
const BidSummary = ItemWinnerFormBidSummary;
const BidderInfo = ItemWinnerFormBidderInfo;

export {
    useBidderSelect,
    getSelectedBidderLabel,
    //
    Root,
    BidderSelect,
    StepDescription,
    DeclareWinnerStep,
    EnterInfoStep,
    ChoosePaymentStep,
    FinalizeWinnerStep,
    NextButton,
    BidSummary,
    BidderInfo,
    //
    ItemWinnerForm,
    ItemWinnerFormBidderSelect,
    ItemWinnerFormStepDescription,
    ItemWinnerFormDeclareWinnerStep,
    ItemWinnerFormEnterInfoStep,
    ItemWinnerFormChoosePaymentStep,
    ItemWinnerFormFinalizeWinnerStep,
    ItemWinnerFormNextButton,
    ItemWinnerFormBidSummary,
    ItemWinnerFormBidderInfo,
};
